import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { Star, Heart, Bookmark, MapPin, Users, Flame, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { getAllLocations } from "redux/features/location/locationSlice";
// import { Location } from 'data/types'; // Unused import

interface Space {
  id: string;
  title: string;
  image: string;
  rating: number;
  price: number;
  location: string;
  capacity: number;
  isPopular?: boolean;
  isFavorite?: boolean;
  isBookmarked?: boolean;
}

interface ModernSpacesSectionProps {
  className?: string;
}

const ModernSpacesSection: React.FC<ModernSpacesSectionProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const { allLocations } = useAppSelector((state) => state.location);
  const navigate = useNavigate();

  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [bookmarks, setBookmarks] = useState<Set<string>>(new Set());
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Fetch real locations data
  useEffect(() => {
    dispatch(getAllLocations());
  }, [dispatch]);

  // Mock data as fallback (kept for fallback purposes)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mockSpaces: Space[] = [
    {
      id: "1",
      title: "Loft Moderne avec Vue Panoramique sur Paris 9è",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400",
      rating: 4.9,
      price: 75,
      location: "Paris 9è",
      capacity: 50,
      isPopular: true,
    },
    {
      id: "2",
      title: "Studio Photo Professionnel Équipé Lyon Centre",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400",
      rating: 4.8,
      price: 120,
      location: "Lyon 2è",
      capacity: 30,
      isPopular: true,
    },
    {
      id: "3",
      title: "Grande Salle de Réception Élégante Marseille",
      image: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400",
      rating: 5.0,
      price: 95,
      location: "Marseille",
      capacity: 80,
      isPopular: false,
    },
    {
      id: "4",
      title: "Espace Co-working Design et Moderne Bordeaux",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400",
      rating: 4.7,
      price: 180,
      location: "Bordeaux",
      capacity: 25,
      isPopular: true,
    },
    {
      id: "5",
      title: "Villa Provençale avec Jardin Méditerranéen Nice",
      image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400",
      rating: 4.9,
      price: 200,
      location: "Nice",
      capacity: 100,
      isPopular: true,
    },
    {
      id: "6",
      title: "Château Historique pour Événements Prestige",
      image: "https://images.unsplash.com/photo-1520637836862-4d197d17c13a?w=400",
      rating: 5.0,
      price: 350,
      location: "Versailles",
      capacity: 200,
      isPopular: false,
    },
  ];

  // Transform Location data to Space format for display
  const transformLocationToSpace = (location: any): Space => ({
    id: location.id,
    title: location.title,
    image:
      location.images && location.images.length > 0
        ? `${process.env.REACT_APP_CLOUD_FRONT}${location.images[0]}`
        : "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400",
    rating: 4.5 + Math.random() * 0.5, // Random rating between 4.5-5.0
    price: parseInt(location.price) || 75,
    location: `${location.city}, ${location.region}`,
    capacity: location.visitor || 50,
    isPopular: Math.random() > 0.5, // Random popularity
  });

  // Transform real API data to Space format
  // Check if allLocations has a locations array or if it IS the locations array
  let locationsArray = [];
  if (Array.isArray(allLocations)) {
    locationsArray = allLocations;
  } else if (allLocations?.locations && Array.isArray(allLocations.locations)) {
    locationsArray = allLocations.locations;
  }

  const realSpaces = locationsArray.length > 0 ? locationsArray.map(transformLocationToSpace) : [];

  // Use real spaces if available, otherwise show message that mock spaces are being used
  const displaySpaces = realSpaces.length > 0 ? realSpaces.slice(0, 6) : [];

  const slidesToShow = 4; // Number of cards visible at once
  const totalSlides = Math.ceil(displaySpaces.length / slidesToShow);

  // Auto-carousel
  useEffect(() => {
    if (!isAutoPlay) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);

    return () => clearInterval(timer);
  }, [totalSlides, isAutoPlay]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setIsAutoPlay(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setIsAutoPlay(false);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlay(false);
  };

  const toggleFavorite = (spaceId: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(spaceId)) {
        newFavorites.delete(spaceId);
      } else {
        newFavorites.add(spaceId);
      }
      return newFavorites;
    });
  };

  const toggleBookmark = (spaceId: string) => {
    setBookmarks((prev) => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(spaceId)) {
        newBookmarks.delete(spaceId);
      } else {
        newBookmarks.add(spaceId);
      }
      return newBookmarks;
    });
  };

  return (
    <div
      className={`py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 ${className}`}
      ref={ref}
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <Flame className="w-6 h-6 text-[#4663ac]" />
            <span className="text-[#4663ac] font-semibold uppercase tracking-wide text-sm">Tendance</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Espaces{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4663ac] to-[#c1d8f0]">
              Tendance
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Découvrez les espaces les plus populaires et les mieux notés par notre communauté
          </p>
        </motion.div>

        {/* Show loading or no data message */}
        {displaySpaces.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {allLocations ? "Aucun espace disponible pour le moment" : "Chargement des espaces..."}
            </p>
          </div>
        )}

        {/* Slider Container */}
        {displaySpaces.length > 0 && (
          <div className="relative mb-12">
            {/* Navigation Arrows */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 z-10 w-12 h-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-[#4663ac] dark:hover:text-[#c1d8f0] transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 z-10 w-12 h-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-[#4663ac] dark:hover:text-[#c1d8f0] transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>

            {/* Slider */}
            <div
              className="overflow-hidden"
              onMouseEnter={() => setIsAutoPlay(false)}
              onMouseLeave={() => setIsAutoPlay(true)}
            >
              <motion.div
                ref={sliderRef}
                className="flex"
                animate={{ x: `-${currentSlide * 100}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                  <div key={slideIndex} className="w-full flex-shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {displaySpaces
                        .slice(slideIndex * slidesToShow, (slideIndex + 1) * slidesToShow)
                        .map((space: Space, cardIndex: number) => (
                          <motion.div
                            key={space.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: cardIndex * 0.1 }}
                            whileHover={{
                              scale: 1.05,
                              y: -10,
                            }}
                            className="group relative"
                          >
                            {/* Card with fixed height */}
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden backdrop-blur-lg border border-white/20 h-full flex flex-col">
                              {/* Popular Badge */}
                              {space.isPopular && (
                                <div className="absolute top-4 left-4 z-10">
                                  <div className="bg-gradient-to-r from-[#4663ac] to-[#c1d8f0] text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                                    <Flame className="w-3 h-3" />
                                    <span>Populaire</span>
                                  </div>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="absolute top-4 right-4 z-10 space-y-2">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => toggleFavorite(space.id)}
                                  className={`p-2 rounded-full backdrop-blur-md transition-all ${
                                    favorites.has(space.id)
                                      ? "bg-[#4663ac] text-white"
                                      : "bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-[#4663ac] hover:text-white"
                                  }`}
                                >
                                  <Heart
                                    className="w-4 h-4"
                                    fill={favorites.has(space.id) ? "currentColor" : "none"}
                                  />
                                </motion.button>

                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => toggleBookmark(space.id)}
                                  className={`p-2 rounded-full backdrop-blur-md transition-all ${
                                    bookmarks.has(space.id)
                                      ? "bg-[#c1d8f0] text-white"
                                      : "bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-[#c1d8f0] hover:text-white"
                                  }`}
                                >
                                  <Bookmark
                                    className="w-4 h-4"
                                    fill={bookmarks.has(space.id) ? "currentColor" : "none"}
                                  />
                                </motion.button>
                              </div>

                              {/* Image */}
                              <div className="relative h-48 overflow-hidden flex-shrink-0">
                                <img
                                  src={space.image}
                                  alt={space.title}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                              </div>

                              {/* Content */}
                              <div className="p-6 flex flex-col flex-grow">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                                    <MapPin className="w-4 h-4" />
                                    <span className="text-sm">{space.location}</span>
                                  </div>
                                </div>

                                {/* Title with fixed height and multiline support */}
                                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 group-hover:text-[#4663ac] transition-colors leading-tight h-12 overflow-hidden flex items-start">
                                  <span className="line-clamp-2">{space.title}</span>
                                </h3>

                                <div className="flex items-center justify-between mb-4 mt-auto">
                                  <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                                    <Users className="w-4 h-4" />
                                    <span className="text-sm">{space.capacity} pers.</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                      {space.price}€
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">/h</span>
                                  </div>
                                </div>

                                {/* CTA Button */}
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  className="w-full bg-gradient-to-r from-[#4663ac] to-[#c1d8f0] hover:from-[#3d5497] hover:to-[#b3cfe8] text-white py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 group text-sm"
                                >
                                  <span>Voir les détails</span>
                                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Slider Indicators */}
            <div className="flex justify-center space-x-2 mt-8">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.8 }}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "bg-[#4663ac] shadow-lg"
                      : "bg-gray-300 dark:bg-gray-600 hover:bg-[#c1d8f0] dark:hover:bg-[#4663ac]"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* CSS for line-clamp is handled via Tailwind CSS */}

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/espace-evenementiel")}
            className="inline-flex items-center space-x-2 bg-transparent border-2 border-[#4663ac] text-[#4663ac] hover:bg-[#4663ac] hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300"
          >
            <span>Voir tous les espaces</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default ModernSpacesSection;
